import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Storage key pattern matching useTablePreferences for compatibility
 */
const getStorageKey = (tableId: string) => `table-prefs-${tableId}`;

interface SortPreference {
  column: string;
  direction: 'asc' | 'desc';
}

interface UseSortPreferenceOptions {
  tableId: string;
  defaultColumn?: string;
  defaultDirection?: 'asc' | 'desc';
}

interface UseSortPreferenceReturn {
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (column: string, direction: 'asc' | 'desc') => void;
}

/**
 * Load sort preference from localStorage
 * Compatible with useTablePreferences storage format
 */
function loadSortPreference(
  tableId: string,
  defaultColumn: string,
  defaultDirection: 'asc' | 'desc'
): SortPreference {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    if (!stored) {
      return { column: defaultColumn, direction: defaultDirection };
    }

    const parsed = JSON.parse(stored);
    if (parsed.sort && parsed.sort.column) {
      return {
        column: parsed.sort.column,
        direction: parsed.sort.direction || defaultDirection,
      };
    }

    return { column: defaultColumn, direction: defaultDirection };
  } catch (e) {
    console.warn(`Failed to load sort preference for ${tableId}:`, e);
    return { column: defaultColumn, direction: defaultDirection };
  }
}

/**
 * Save sort preference to localStorage
 * Compatible with useTablePreferences storage format
 */
function saveSortPreference(tableId: string, sort: SortPreference): void {
  try {
    const key = getStorageKey(tableId);
    const existing = localStorage.getItem(key);
    let data: any = {};

    if (existing) {
      try {
        data = JSON.parse(existing);
      } catch {
        data = {};
      }
    }

    // Update only the sort field, preserving other preferences
    data.sort = { column: sort.column, direction: sort.direction };

    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn(`Failed to save sort preference for ${tableId}:`, e);
  }
}

/**
 * Hook for managing sort preference with localStorage persistence.
 * Use this for server-side sorting scenarios where the parent component
 * controls the sort state but needs it persisted across page refreshes.
 *
 * Compatible with useTablePreferences storage format.
 *
 * @example
 * ```tsx
 * const { sortColumn, sortDirection, handleSort } = useSortPreference({
 *   tableId: 'cars-table-all',
 *   defaultColumn: 'ref_no',
 *   defaultDirection: 'asc'
 * });
 *
 * // Pass to CarsTable for server-side sorting
 * <CarsTable
 *   sortColumn={sortColumn}
 *   sortDirection={sortDirection}
 *   onSort={handleSort}
 * />
 * ```
 */
export function useSortPreference({
  tableId,
  defaultColumn = '',
  defaultDirection = 'asc',
}: UseSortPreferenceOptions): UseSortPreferenceReturn {
  // Initialize state from localStorage
  const [sort, setSort] = useState<SortPreference>(() =>
    loadSortPreference(tableId, defaultColumn, defaultDirection)
  );

  // Track if this is the initial mount to avoid unnecessary saves
  const isInitialMount = useRef(true);
  const prevTableIdRef = useRef(tableId);

  // Re-load from localStorage when tableId changes
  useEffect(() => {
    if (prevTableIdRef.current !== tableId) {
      prevTableIdRef.current = tableId;
      setSort(loadSortPreference(tableId, defaultColumn, defaultDirection));
    }
  }, [tableId, defaultColumn, defaultDirection]);

  // Persist to localStorage when sort changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    saveSortPreference(tableId, sort);
  }, [tableId, sort]);

  // Handle sort changes
  const handleSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSort({ column, direction });
  }, []);

  return {
    sortColumn: sort.column,
    sortDirection: sort.direction,
    handleSort,
  };
}
